<template>
  <div class="relative min-h-screen w-full bg-gradient-to-br from-gray-50 to-blue-50">
    <div class="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <!-- Breadcrumb -->
      <nav class="text-sm text-gray-600 mb-8 flex items-center">
        <router-link to="/dashboard"
          class="hover:text-blue-600 transition-colors duration-200 font-medium flex items-center">
          <font-awesome-icon icon="home" class="mr-1 text-xs" />
          Dashboard
        </router-link>
        <span class="mx-2 text-gray-400">/</span>
        <span class="text-blue-700 font-semibold">Timetable Management</span>
      </nav>

      <!-- Header -->
      <div
        class="header-section bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl shadow-xl p-8 mb-8 text-white relative overflow-hidden">
        <div class="absolute inset-0 bg-black opacity-5"></div>
        <div class="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between">
          <div class="mb-4 md:mb-0">
            <div class="flex items-center mb-3">
              <div class="bg-white bg-opacity-20 rounded-xl p-3 mr-4">
                <font-awesome-icon icon="calendar-alt" class="text-2xl" />
              </div>
              <h1 class="text-3xl md:text-4xl font-bold">Student Time Table Management</h1>
            </div>
            <p class="text-blue-100 text-lg mt-2">Manage institutions, campuses, programs, and time tables</p>
          </div>
        </div>
      </div>

      <!-- Tabs Navigation -->
      <div class="bg-white rounded-xl shadow-lg mb-6 overflow-hidden">
        <div class="border-b border-gray-100">
          <nav class="flex overflow-x-auto scrollbar-hide">
            <button v-for="tab in tabs" :key="tab.id" @click="activeTab = tab.id" :class="[
              'px-6 py-4 text-sm font-semibold border-b-3 transition-all duration-200 whitespace-nowrap flex items-center',
              activeTab === tab.id
                ? 'border-blue-600 text-blue-600 bg-blue-50'
                : 'border-transparent text-gray-500 hover:text-blue-600 hover:bg-gray-50'
            ]">
              <font-awesome-icon :icon="tab.icon" class="mr-2" />
              {{ tab.label }}
            </button>
          </nav>
        </div>
      </div>

      <!-- Tab Content -->
      <div class="bg-white rounded-xl shadow-lg p-8">
        <keep-alive>
          <component :is="activeTabComponent" v-bind="tabProps" @refresh="handleRefresh" />
        </keep-alive>
      </div>
    </div>
  </div>
</template>

<script>
import { gql } from 'graphql-tag';
import InstitutionsTab from './components/InstitutionsTab.vue';
import DepartmentsTab from './components/DepartmentsTab.vue';
import ProgramsTab from './components/ProgramsTab.vue';
import SemestersTab from './components/SemestersTab.vue';
import DefaultConfigurationTab from './components/DefaultConfigurationTab.vue';

export default {
  name: 'TimetableManagement',
  components: {
    InstitutionsTab,
    DepartmentsTab,
    ProgramsTab,
    SemestersTab,
    DefaultConfigurationTab
  },
  data() {
    return {
      activeTab: 'institutions',
      tabs: [
        { id: 'institutions', label: 'Institutions & Campuses', icon: 'building' },
        { id: 'departments', label: 'Departments', icon: 'sitemap' },
        { id: 'programs', label: 'Programs', icon: 'graduation-cap' },
        { id: 'semesters', label: 'Time Tables', icon: 'calendar-week' },
        { id: 'configuration', label: 'Configuration', icon: 'cog' }
      ],
      // Shared data
      institutions: [],
      allCampuses: [],
      allDepartments: [],
      allPrograms: [],
      allSemesters: [],
      // Loading states
      loadingInstitutions: false,
      loadingDepartments: false,
      loadingPrograms: false,
      loadingSemesters: false
    };
  },
  computed: {
    activeTabComponent() {
      const componentMap = {
        institutions: 'InstitutionsTab',
        departments: 'DepartmentsTab',
        programs: 'ProgramsTab',
        semesters: 'SemestersTab',
        configuration: 'DefaultConfigurationTab'
      };
      return componentMap[this.activeTab] || 'InstitutionsTab';
    },
    tabProps() {
      const baseProps = {
        // Institutions Tab
        institutions: this.institutions,
        loading: this.loadingInstitutions,
        // Departments Tab
        allDepartments: this.allDepartments,
        // Programs Tab
        allPrograms: this.allPrograms,
        // Semesters Tab
        allSemesters: this.allSemesters,
        allCampuses: this.allCampuses,
      };

      // Return specific props based on active tab
      switch (this.activeTab) {
        case 'institutions':
          return {
            institutions: this.institutions,
            loading: this.loadingInstitutions
          };
        case 'departments':
          return {
            institutions: this.institutions,
            allCampuses: this.allCampuses,
            loading: this.loadingDepartments
          };
        case 'programs':
          return {
            allDepartments: this.allDepartments,
            loading: this.loadingPrograms
          };
        case 'semesters':
          return {
            institutions: this.institutions,
            allCampuses: this.allCampuses,
            allDepartments: this.allDepartments,
            allPrograms: this.allPrograms,
            loading: this.loadingSemesters
          };
        default:
          return baseProps;
      }
    }
  },
  mounted() {
    this.loadData();
    this.loadDepartments();
    this.loadPrograms();
    this.loadSemesters();
  },
  methods: {
    async loadDepartments() {
      this.loadingDepartments = true;
      try {
        const response = await this.$apollo.query({
          query: gql`
            query {
              programDepartments {
                error
                error_code
                message
                data {
                  id
                  entity
                  campus
                  prg_dpt
                  name
                  is_active
                }
              }
            }
          `,
          fetchPolicy: 'network-only'
        });

        if (response.data.programDepartments.error) {
          this.allDepartments = [];
          return;
        }

        this.allDepartments = response.data.programDepartments.data || [];
      } catch (error) {
        console.error('Failed to load departments:', error);
        this.allDepartments = [];
      } finally {
        this.loadingDepartments = false;
      }
    },
    async loadPrograms() {
      this.loadingPrograms = true;
      try {
        const response = await this.$apollo.query({
          query: gql`
            query GetPrograms($program_department_id: ID, $entity: String, $is_active: Boolean) {
              programs(program_department_id: $program_department_id, entity: $entity, is_active: $is_active) {
                error
                error_code
                message
                data {
                  id
                  program_department_id
                  name
                  is_active
                  program_department {
                    id
                    entity
                    campus
                    prg_dpt
                    name
                  }
                }
              }
            }
          `,
          variables: {
            program_department_id: null,
            entity: null,
            is_active: null
          },
          fetchPolicy: 'network-only'
        });

        if (response.data.programs.error) {
          this.allPrograms = [];
          return;
        }

        this.allPrograms = response.data.programs.data || [];
      } catch (error) {
        console.error('Failed to load programs:', error);
        this.allPrograms = [];
      } finally {
        this.loadingPrograms = false;
      }
    },
    async loadSemesters() {
      this.loadingSemesters = true;
      try {
        const response = await this.$apollo.query({
          query: gql`
            query {
              programSemesters {
                error
                error_code
                message
                data {
                  id
                  token
                  program_id
                  name
                  start_date
                  end_date
                  is_active
                  program {
                    id
                    name
                    program_department {
                      id
                      entity
                      campus
                      name
                    }
                  }
                }
              }
            }
          `,
          fetchPolicy: 'network-only'
        });

        if (response.data.programSemesters.error) {
          this.allSemesters = [];
          return;
        }

        this.allSemesters = response.data.programSemesters.data || [];
      } catch (error) {
        console.error('Failed to load semesters:', error);
        this.allSemesters = [];
      } finally {
        this.loadingSemesters = false;
      }
    },
    async loadData() {
      this.loadingInstitutions = true;
      try {
        const [entitiesResponse, campusesResponse] = await Promise.all([
          this.$apollo.query({
            query: gql`
              query GetEntities {
                entities {
                  error
                  message
                  data {
                    id
                    entity
                    name
                  }
                }
              }
            `,
            fetchPolicy: 'network-only'
          }),
          this.$apollo.query({
            query: gql`
              query GetCampuses {
                campuses {
                  error
                  message
                  data {
                    id
                    Entity
                    portal_campus
                    EnglishName
                  }
                }
              }
            `,
            fetchPolicy: 'network-only'
          })
        ]);

        if (entitiesResponse.data.entities.error || campusesResponse.data.campuses.error) {
          this.institutions = [];
          return;
        }

        const entities = entitiesResponse.data.entities.data || [];
        const campuses = campusesResponse.data.campuses.data || [];
        const institutionOrder = ['FCHS', 'ADPOLY', 'ATS', 'ADVETI'];

        const institutionsList = entities.map(entity => {
          const entityCode = entity.entity ? entity.entity.trim() : '';
          const entityCampuses = campuses
            .filter(campus => {
              if (!campus.Entity || !campus.portal_campus) return false;
              return campus.Entity.trim() === entityCode;
            })
            .map(campus => ({
              id: campus.id,
              name: campus.portal_campus,
              englishName: campus.EnglishName
            }));

          return {
            id: entity.id,
            name: entity.name,
            entity: entity.entity,
            campuses: entityCampuses
          };
        });

        this.institutions = institutionsList.sort((a, b) => {
          const getOrderIndex = (institution) => {
            const nameIndex = institutionOrder.indexOf(institution.name);
            if (nameIndex !== -1) return nameIndex;
            return institutionOrder.indexOf(institution.entity);
          };
          const indexA = getOrderIndex(a);
          const indexB = getOrderIndex(b);
          if (indexA !== -1 && indexB !== -1) {
            return indexA - indexB;
          }
          return a.name.localeCompare(b.name);
        });

        this.allCampuses = campuses.filter(campus => campus.portal_campus).map(campus => ({
          id: campus.id,
          name: campus.portal_campus,
          entity: campus.Entity ? campus.Entity.trim() : ''
        }));
      } catch (error) {
        console.error('Failed to load data:', error);
        this.institutions = [];
      } finally {
        this.loadingInstitutions = false;
      }
    },
    handleRefresh(tab) {
      // Handle refresh events from child components
      switch (tab) {
        case 'institutions':
          this.loadData();
          break;
        case 'departments':
          this.loadDepartments();
          break;
        case 'programs':
          this.loadPrograms();
          this.loadDepartments(); // Refresh departments too in case new ones were added
          break;
        case 'semesters':
          this.loadSemesters();
          this.loadPrograms(); // Refresh programs too in case new ones were added
          break;
        default:
          break;
      }
    }
  }
};
</script>

<style scoped>
.tab-content {
  animation: fadeIn 0.4s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.scrollbar-hide::-webkit-scrollbar {
  display: none;
}
</style>
